import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import { radialPadTooltip } from './radialControlPad.constants.ts';

type TimeoutHandle = ReturnType<typeof setTimeout> | null;

/**
 * Sector tooltip visibility with delayed show on hover and auto-hide after `visibleMs`.
 * Pointer leave clears immediately; moving between sectors hides the current tooltip and resets timers.
 */
export const useDelayedSectorTooltip = (
  showDelayMs: number = radialPadTooltip.showDelayMs,
  visibleMs: number = radialPadTooltip.visibleMs,
  /** When true, clears any visible tooltip and pending show/hide timers (e.g. pad auto-hide). */
  suppressed?: boolean
) => {
  const [tooltipSectorId, setTooltipSectorId] = useState<string | null>(null);
  const showTimerRef = useRef<TimeoutHandle>(null);
  const hideTimerRef = useRef<TimeoutHandle>(null);

  const clearTimers = useCallback(() => {
    clearTimerRef(showTimerRef);
    clearTimerRef(hideTimerRef);
  }, []);

  const onHoveredSectorChange = useCallback(
    (sectorId: string | null) => {
      clearTimers();
      setTooltipSectorId(null);

      if (sectorId == null) {
        return;
      }

      showTimerRef.current = setTimeout(() => {
        setTooltipSectorId(sectorId);
        showTimerRef.current = null;

        hideTimerRef.current = setTimeout(() => {
          setTooltipSectorId(null);
          hideTimerRef.current = null;
        }, visibleMs);
      }, showDelayMs);
    },
    [clearTimers, showDelayMs, visibleMs]
  );

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (!suppressed) {
      return;
    }
    clearTimers();
    setTooltipSectorId(null);
  }, [suppressed, clearTimers]);

  return { tooltipSectorId, onHoveredSectorChange };
};

const clearTimerRef = (timerRef: RefObject<TimeoutHandle>) => {
  if (timerRef.current != null) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};
